/**
 * Inbox advanced-filter document — a versioned value the web filter panel
 * (`InboxFilterPanel`) edits and evaluates CLIENT-SIDE over the complete
 * inbox list. One schema + ONE evaluator, both in `@releve/shared` (the
 * surveys / recurring template on `filterDocumentPrimitives.ts`).
 *
 * The document is AD HOC (component state for the session) and has NO seeded
 * default — `EMPTY_INBOX_FILTER_DOCUMENT` is the only starting value.
 *
 * ── v1 vocabulary ───────────────────────────────────────────────────────────
 *  - `inbox.status`           string family over the status token
 *                             (`unread` / `read` / `processed`, …).
 *  - `inbox.type`             string family over the message type; an unset
 *                             `message_type` reads as `expense` (what the
 *                             grid shows for it), never as empty.
 *  - `inbox.has_attachments`  string family over a DERIVED token: `'yes'`
 *                             when the email has at least one attachment,
 *                             else `'no'` (never `''`). "Any" is spelled as
 *                             NO condition.
 *  - `inbox.received_on`      DATE family over `received_at`.
 *
 * ── THE DATE IS THE VIEWER'S LOCAL CALENDAR DAY ─────────────────────────────
 * `received_at` is a `timestamptz` instant. The inbox is per-user, not
 * org-shared, so the meaningful day is the VIEWER's own: the grid's Received
 * cell renders it with `safeFormatDate` (local), and this evaluator reduces
 * the instant with `toLocalCalendarDay` (local Date components) — the same
 * day, so a row never displays as one day and filters as another. This is a
 * recorded choice (docs/INBOX_FEATURE.md); the org-zone rule in docs/DATES.md
 * is for org-shared records.
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b the grid runs the panel only — ZERO
 * filterable columns.
 *
 * `countActiveInboxConditions` carries the entity in its name because the
 * bare `countActiveConditions` is already exported from the barrel.
 */
import { z } from "zod";
import { makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1)
// ---------------------------------------------------------------------------
export const INBOX_FILTER_STRING_FIELDS = [
    "inbox.status",
    "inbox.type",
    "inbox.has_attachments",
];
export const INBOX_FILTER_DATE_FIELDS = ["inbox.received_on"];
/** The statuses the panel offers (the list never shows archived / spam). */
export const INBOX_FILTER_STATUSES = ["unread", "read", "processed"];
/** The message types the panel offers. */
export const INBOX_FILTER_TYPES = ["expense", "invitation"];
/** The two derived `inbox.has_attachments` tokens. */
export const INBOX_ATTACHMENT_STATES = ["yes", "no"];
// ---------------------------------------------------------------------------
// Conditions + document
// ---------------------------------------------------------------------------
const stringConditionSchema = makeStringConditionSchema(INBOX_FILTER_STRING_FIELDS);
const dateConditionSchema = makeDateConditionSchema(INBOX_FILTER_DATE_FIELDS);
/** Disjoint op sets ('in'/'is_empty' vs the three date ops) — `op` alone narrows. */
export const inboxFilterConditionSchema = z.union([stringConditionSchema, dateConditionSchema]);
export const inboxFilterDocumentSchema = makeFilterDocumentSchema(inboxFilterConditionSchema);
/** The empty document — "no filters active" (what Clear resets to). */
export const EMPTY_INBOX_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/** The message type a row shows as (an unset type is an expense email). */
export function inboxMessageType(row) {
    return row.message_type || "expense";
}
/** The derived attachment-state token of a row. */
export function inboxAttachmentState(row) {
    return (row.attachments?.length ?? 0) > 0 ? "yes" : "no";
}
/* Exhaustive switches with NO default: a new field is a TS2366 until handled. */
function stringValue(row, field) {
    switch (field) {
        case "inbox.status":
            return row.status ?? "";
        case "inbox.type":
            return inboxMessageType(row);
        case "inbox.has_attachments":
            return inboxAttachmentState(row);
    }
}
function dateValue(row, field) {
    switch (field) {
        case "inbox.received_on":
            return toLocalCalendarDay(row.received_at);
    }
}
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to inbox rows (AND of all conditions — v1 `match`
 * is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applyInboxFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveInboxConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=inboxFilters.js.map